import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ArrowDown, ArrowUp, Coins, GripVertical } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import Flag from "react-world-flags";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { useBalancesHidden } from "@/shared/hooks/useBalancesHidden";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { getColorForCurrency } from "../constants/walletColors.constants";
import { isCryptoCurrency } from "../constants/walletMethods.constants";
import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
} from "../constants/wallets.constants";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";
import { moveItem } from "../utils/walletOrder";

export interface WalletOrderEditorProps {
  wallets: RuntimeWallet[];
  isSaving: boolean;
  /** Receives the ids first to last. Only called when the order changed. */
  onSave: (walletIds: string[]) => void;
  onCancel: () => void;
}

/**
 * The wallets grid while the organization arranges it: one compact, numbered row per wallet — the number is
 * the position it will have — moved by dragging the grip or with the arrows. The arrows are the keyboard path
 * (the grip is pointer-only and hidden from assistive tech), and every move is announced.
 *
 * Nothing is saved until "Save order"; cancelling drops the draft.
 */
export function WalletOrderEditor({ wallets, isSaving, onSave, onCancel }: WalletOrderEditorProps) {
  const { t } = useTranslations("wallets");
  const [order, setOrder] = useState(() => wallets.map((wallet) => wallet.id));
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");

  const byId = useMemo(() => new Map(wallets.map((wallet) => [wallet.id, wallet])), [wallets]);
  // A wallet that disappeared meanwhile (deleted in another tab) is dropped from the draft rather than saved.
  const rows = order.flatMap((id) => {
    const wallet = byId.get(id);
    return wallet ? [wallet] : [];
  });
  const changed = rows.some((wallet, index) => wallet.id !== wallets[index]?.id);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const move = useCallback(
    (from: number, to: number) => {
      setOrder((current) => moveItem(current, from, to));
      const wallet = byId.get(order[from]);
      if (wallet && to >= 0 && to < order.length) {
        setAnnouncement(t("order_moved_announcement", { currency: wallet.currency, position: to + 1 }));
      }
    },
    [byId, order, t],
  );

  const handleDragStart = ({ active }: DragStartEvent) => setDraggingId(String(active.id));

  // The list follows the pointer while dragging, so the gap opens where the row will land.
  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || active.id === over.id) return;
    setOrder((current) => {
      const from = current.indexOf(String(active.id));
      const to = current.indexOf(String(over.id));
      return from === -1 || to === -1 ? current : moveItem(current, from, to);
    });
  };

  const handleDragEnd = ({ active }: DragEndEvent) => {
    setDraggingId(null);
    const wallet = byId.get(String(active.id));
    if (wallet) {
      setAnnouncement(
        t("order_moved_announcement", { currency: wallet.currency, position: order.indexOf(wallet.id) + 1 }),
      );
    }
  };

  const dragging = draggingId ? byId.get(draggingId) : undefined;

  return (
    <section className="space-y-3" aria-labelledby="wallet-order-title">
      <div>
        <h2 id="wallet-order-title" className="text-base font-semibold text-foreground">
          {t("order_title")}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("order_subtitle")}</p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingId(null)}
      >
        <ol className="max-w-2xl space-y-2">
          {rows.map((wallet, index) => (
            <WalletOrderRow
              key={wallet.id}
              wallet={wallet}
              position={index + 1}
              isFirst={index === 0}
              isLast={index === rows.length - 1}
              disabled={isSaving}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
            />
          ))}
        </ol>
        <DragOverlay dropAnimation={null}>
          {dragging ? (
            <WalletOrderRowFace
              wallet={dragging}
              position={order.indexOf(dragging.id) + 1}
              className="cursor-grabbing border-primary/40 shadow-lg ring-1 ring-primary/20"
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      <div className="flex max-w-2xl flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <ColocaButton variant="ghost" className="w-full sm:w-auto" onClick={onCancel} disabled={isSaving}>
          {t("order_cancel")}
        </ColocaButton>
        <ColocaButton
          variant="primary"
          className="w-full sm:w-auto"
          onClick={() => onSave(rows.map((wallet) => wallet.id))}
          disabled={!changed}
          loading={isSaving}
        >
          {t("order_save")}
        </ColocaButton>
      </div>
    </section>
  );
}

interface WalletOrderRowProps {
  wallet: RuntimeWallet;
  position: number;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function WalletOrderRow({
  wallet,
  position,
  isFirst,
  isLast,
  disabled,
  onMoveUp,
  onMoveDown,
}: WalletOrderRowProps) {
  const { t } = useTranslations("wallets");
  const draggable = useDraggable({ id: wallet.id, disabled });
  const droppable = useDroppable({ id: wallet.id });
  const { setNodeRef: setDragRef } = draggable;
  const { setNodeRef: setDropRef } = droppable;
  const setRef = useCallback(
    (node: HTMLLIElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  return (
    <li ref={setRef}>
      <WalletOrderRowFace
        wallet={wallet}
        position={position}
        className={cn(draggable.isDragging && "border-dashed border-primary/40 bg-muted/40 opacity-50")}
        handle={
          <span
            // Pointer-only: the arrows next to the row are the keyboard path, so the grip stays out of the tab
            // order instead of announcing a "draggable" that no key can move.
            {...draggable.listeners}
            aria-hidden="true"
            className={cn(
              "grid h-8 w-6 shrink-0 touch-none place-items-center rounded text-muted-foreground",
              disabled ? "cursor-not-allowed opacity-40" : "cursor-grab hover:bg-muted hover:text-foreground",
            )}
          >
            <GripVertical size={16} />
          </span>
        }
        actions={
          <div className="flex shrink-0 items-center gap-0.5">
            <ColocaIconButton
              icon={ArrowUp}
              variant="ghost"
              size="sm"
              aria-label={t("order_move_up_aria", { currency: wallet.currency })}
              disabled={disabled || isFirst}
              onClick={onMoveUp}
            />
            <ColocaIconButton
              icon={ArrowDown}
              variant="ghost"
              size="sm"
              aria-label={t("order_move_down_aria", { currency: wallet.currency })}
              disabled={disabled || isLast}
              onClick={onMoveDown}
            />
          </div>
        }
      />
    </li>
  );
}

interface WalletOrderRowFaceProps {
  wallet: RuntimeWallet;
  position: number;
  className?: string;
  handle?: ReactNode;
  actions?: ReactNode;
}

/** What a row shows, shared by the row in the list and the copy that follows the pointer while dragging. */
function WalletOrderRowFace({ wallet, position, className, handle, actions }: WalletOrderRowFaceProps) {
  const { activeLanguage } = useTranslations("wallets");
  const { hidden: balancesHidden } = useBalancesHidden();
  const isCrypto = isCryptoCurrency(wallet.currency);
  const accentColor = getColorForCurrency(wallet.currency);
  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? "";
  const flagCode = !isCrypto
    ? (CURRENCY_TO_FLAG_CODE[wallet.currency] ?? CURRENCY_TO_DISPERSION_CODE[wallet.currency] ?? null)
    : null;
  const decimals = currencyFractionDigits(wallet.currency);

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-lg border border-border bg-card px-2 py-2 shadow-sm sm:gap-3 sm:px-3",
        className,
      )}
    >
      {handle ?? (
        <span className="grid h-8 w-6 shrink-0 place-items-center text-muted-foreground" aria-hidden="true">
          <GripVertical size={16} />
        </span>
      )}
      <span
        className="w-5 shrink-0 text-center font-mono text-xs tabular-nums text-muted-foreground"
        aria-hidden="true"
      >
        {position}
      </span>
      <div
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg border"
        style={{
          backgroundColor: `color-mix(in oklch, ${accentColor} 10%, transparent)`,
          borderColor: `color-mix(in oklch, ${accentColor} 24%, transparent)`,
        }}
        aria-hidden="true"
      >
        {isCrypto || !flagCode ? (
          <Coins size={16} style={{ color: accentColor }} />
        ) : (
          <Flag code={flagCode} className="h-4 w-6 rounded-[2px] object-cover" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{countryName || wallet.currency}</p>
        <p className="font-mono text-[11px] tracking-wide text-muted-foreground">{wallet.currency}</p>
      </div>
      <ColocaAmountDisplay
        value={wallet.balance_available}
        masked={balancesHidden}
        currency={wallet.currency}
        minimumFractionDigits={decimals.min}
        maximumFractionDigits={decimals.max}
        locale={resolveLocale(activeLanguage)}
        size="sm"
        className="hidden min-w-0 shrink truncate text-right text-muted-foreground sm:block"
      />
      {actions}
    </div>
  );
}
